// Create a game: lobby name, public/private (password), and the game settings
// (ante, timers, hand limit, spectators, spell rules) in one window.
import { useState, type FormEvent } from "react";
import {
  CreateLobbyBodySchema,
  DEFAULT_LOBBY_SETTINGS,
  LOBBY_NAME_MAX,
  LOBBY_NAME_MIN,
  LOBBY_PASSWORD_MAX,
  LOBBY_PASSWORD_MIN,
  type CreateLobbyBody,
  type LobbySettingsPatch,
} from "@wp/shared";
import { settingsPatch, toDraft, type SettingsDraft } from "../../game/lobbySettings";
import { Modal } from "../common/Modal";
import { GameSettingsFields, settingsProblem } from "../game/Dialogs";

export function CreateGameWindow({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  /** Resolves to an error message, or null on success. */
  onCreate: (body: CreateLobbyBody, settings: LobbySettingsPatch) => Promise<string | null>;
}) {
  const [name, setName] = useState("");
  const [isPrivate, setPrivate] = useState(false);
  const [password, setPassword] = useState("");
  const [draft, setDraft] = useState<SettingsDraft>(() => toDraft(DEFAULT_LOBBY_SETTINGS));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (p: Partial<SettingsDraft>) => setDraft((d) => ({ ...d, ...p }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const parsed = CreateLobbyBodySchema.safeParse(isPrivate ? { name, isPrivate, password } : { name, isPrivate });
    if (!parsed.success) {
      const path = parsed.error.issues[0]?.path[0];
      setError(
        path === "password"
          ? `Private games need a password (${LOBBY_PASSWORD_MIN}-${LOBBY_PASSWORD_MAX} characters).`
          : `Give the game a name (${LOBBY_NAME_MIN}-${LOBBY_NAME_MAX} characters).`,
      );
      return;
    }
    const checked = settingsProblem(settingsPatch(DEFAULT_LOBBY_SETTINGS, draft));
    if (!checked.patch) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    const problem = await onCreate(parsed.data, checked.patch);
    setBusy(false);
    if (problem) setError(problem);
  }

  return (
    <Modal title="Create a game" onClose={onClose} testId="create-lobby-dialog" className="wide">
      <form onSubmit={(e) => void submit(e)} className="stack-form" noValidate>
        <div className="group-box">
          <label className="field">
            <span>Lobby name</span>
            <input data-testid="create-lobby-name" value={name} maxLength={LOBBY_NAME_MAX} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <label className="toggle-row">
            <input type="checkbox" data-testid="create-lobby-private" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
            <span>Private (players need the password to join)</span>
          </label>
          {isPrivate && (
            <label className="field">
              <span>Lobby password (1-{LOBBY_PASSWORD_MAX} characters)</span>
              <input
                data-testid="create-lobby-password"
                type="password"
                autoComplete="new-password"
                value={password}
                maxLength={LOBBY_PASSWORD_MAX}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
          )}
        </div>
        <div className="group-box">
          <GameSettingsFields draft={draft} set={set} lockGame={false} canEdit />
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy} data-testid="create-lobby-submit">
            {busy ? "Creating…" : "Create game"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
